// apps/provider/src/services/api/flowSheet.js
// Flow sheet entry CRUD — longitudinal clinical data points tracked across visits.
//
// Methods: getFlowSheetEntries, getLatestFlowSheetEntries, createFlowSheetEntry, getFlowSheetEntry,
//          updateFlowSheetEntry, deleteFlowSheetEntry,
//          getStandaloneExercises, createStandaloneExercise,
//          getHepShareLink, getHepPublic, sendHepToPatient
//
// Response types: @prism-ehr/shared → FlowSheetEntry, FlowSheetEntryListResponse

/**
 * @returns {Object} Flow sheet domain methods (bound to `this` of the composed apiService)
 */
export function createFlowSheetMethods() {
  return {

    /**
     * Get flow sheet entries with optional filters.
     *
     * Xano: GET /flow-sheet-entry
     * Xano staging file: flow_sheet_entry_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.patient_id]
     * @param {number} [filters.case_id]
     * @param {string} [filters.encounter_id]
     * @param {string} [filters.clinical_note_id]
     * @param {string} [filters.flow_item_id]
     * @param {string} [filters.exercise_id]
     * @param {string} [filters.data_type]
     * @param {boolean} [filters.is_active]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<import('@prism-ehr/shared').FlowSheetEntryListResponse>}
     */
    getFlowSheetEntries(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.case_id != null) queryParams.append('case_id', filters.case_id);
      if (filters.encounter_id) queryParams.append('encounter_id', filters.encounter_id);
      if (filters.clinical_note_id) queryParams.append('clinical_note_id', filters.clinical_note_id);
      if (filters.flow_item_id) queryParams.append('flow_item_id', filters.flow_item_id);
      if (filters.exercise_id) queryParams.append('exercise_id', filters.exercise_id);
      if (filters.data_type) queryParams.append('data_type', filters.data_type);
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);
      if (filters.standalone) queryParams.append('standalone', 'true');
      if (filters.exclude_discontinued) queryParams.append('exclude_discontinued', 'true');
      if (filters.unlinked) queryParams.append('unlinked', 'true');
      if (filters.entry_source) queryParams.append('entry_source', filters.entry_source);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      return this.request(`/flow-sheet-entry?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Latest recorded value per measurement item (non-exercise) for a case, or
     * for a patient when no case is given. Backs the `latest_recorded` template
     * carryForward source.
     *
     * Xano: GET /flow-sheet-entry-latest
     * Xano file: flow-sheet-entry-latest_GET.xs (both envs)
     *
     * @param {Object} scope - { case_id } or { patient_id }
     * @param {number} [scope.case_id]
     * @param {string} [scope.patient_id]
     * @returns {Promise<{ items: import('@prism-ehr/shared').FlowSheetEntry[] }>}
     *   One row per (flow_item_id, side); `value_json` may arrive as a JSON string.
     */
    getLatestFlowSheetEntries(scope = {}) {
      const queryParams = new URLSearchParams();
      if (scope.case_id != null && scope.case_id !== '') queryParams.append('case_id', scope.case_id);
      else if (scope.patient_id) queryParams.append('patient_id', scope.patient_id);

      return this.request(`/flow-sheet-entry-latest?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new flow sheet entry.
     *
     * Xano: POST /flow-sheet-entry
     * Xano staging file: flow_sheet_entry_POST.xs
     *
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').FlowSheetEntry>}
     */
    createFlowSheetEntry(data) {
      return this.request('/flow-sheet-entry', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get a single flow sheet entry by ID.
     *
     * Xano: GET /flow-sheet-entry/{entry_id}
     * Xano staging file: flow_sheet_entry_entry_id_GET.xs
     *
     * @param {string} entryId
     * @returns {Promise<import('@prism-ehr/shared').FlowSheetEntry>}
     */
    getFlowSheetEntry(entryId) {
      return this.request(`/flow-sheet-entry/${entryId}`, {
        method: 'GET'
      });
    },

    /**
     * Update a flow sheet entry.
     *
     * Xano: PATCH /flow-sheet-entry/{entry_id}
     * Xano staging file: flow_sheet_entry_entry_id_PATCH.xs
     *
     * @param {string} entryId
     * @param {Object} updates
     * @returns {Promise<import('@prism-ehr/shared').FlowSheetEntry>}
     */
    updateFlowSheetEntry(entryId, updates) {
      return this.request(`/flow-sheet-entry/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
    },

    /**
     * Delete a flow sheet entry.
     *
     * Xano: DELETE /flow-sheet-entry/{entry_id}
     * Xano staging file: flow_sheet_entry_entry_id_DELETE.xs
     *
     * @param {string} entryId
     * @returns {Promise<void>}
     */
    deleteFlowSheetEntry(entryId) {
      return this.request(`/flow-sheet-entry/${entryId}`, {
        method: 'DELETE'
      });
    },

    /**
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared').FlowSheetEntry[]>}
     */
    getStandaloneExercises(patientId) {
      return this.getFlowSheetEntries({ patient_id: patientId, standalone: true });
    },

    /**
     * @param {Object} data
     * @param {string} data.patient_id
     * @param {string} data.exercise_id
     * @param {string} data.label
     * @param {Object} [data.value_json]
     * @param {number} [data.case_id]
     * @returns {Promise<import('@prism-ehr/shared').FlowSheetEntry>}
     */
    createStandaloneExercise(data) {
      return this.createFlowSheetEntry({
        patient_id: data.patient_id,
        case_id: data.case_id || null,
        exercise_id: data.exercise_id,
        label: data.label,
        data_type: 'exercise',
        value_json: data.value_json || {},
        is_active: true,
      });
    },

    /**
     * Get or create the live public HEP share-link token for a patient.
     *
     * Xano: POST /hep-share-link
     * Xano staging file: hep-share-link_POST.xs
     *
     * @param {string} patientId
     * @returns {Promise<{token: string}>}
     */
    getHepShareLink(patientId) {
      return this.request('/hep-share-link', {
        method: 'POST',
        body: JSON.stringify({ patient_id: patientId })
      });
    },

    /**
     * Fetch the identity-free public HEP payload for a share token (unauthenticated).
     *
     * Xano: GET /public/hep/{token}
     * Xano staging file: public_hep_token_GET.xs
     *
     * @param {string} token
     * @returns {Promise<import('@prism-ehr/shared').PublicHepPayload>}
     */
    getHepPublic(token) {
      return this.publicRequest(`/public/hep/${token}`, {
        method: 'GET'
      });
    },

    /**
     * Send the patient their HEP public link via email and/or SMS (explicit per-click send).
     *
     * Xano: POST /hep-send
     * Xano staging file: hep-send_POST.xs
     *
     * @param {Object} data
     * @param {string} data.patient_id
     * @param {'email'|'sms'|'both'} data.delivery_channel
     * @param {string} [data.email] - Override; defaults to the patient record
     * @param {string} [data.phone] - Override (10 digits); defaults to the patient record
     * @returns {Promise<{success: boolean, email_result: Object|null, sms_result: Object|null}>}
     */
    sendHepToPatient(data) {
      return this.request('/hep-send', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: data.patient_id,
          delivery_channel: data.delivery_channel,
          email: data.email || null,
          phone: data.phone || null
        })
      });
    },
  };
}
